/**
 * Blockchain error code → user-friendly message mapping
 *
 * ARCH-11: Blockchain error codes mapped to user messages
 * Story 2.5 Task 7, Story 2.8 Task 7
 */

import { getNativeCurrencySymbol } from "./wagmi-config";

/** Static error messages (currency-independent) */
const STATIC_CHAIN_ERROR_MESSAGES: Record<string, string> = {
	CHAIN_TX_REVERTED: "Transaction failed on-chain. The contract rejected the operation.",
	CHAIN_GAS_ESTIMATION_FAILED: "Unable to estimate gas. The transaction may fail.",
	CHAIN_USER_REJECTED: "You cancelled the transaction.",
	CHAIN_NO_FEES_TO_CLAIM: "No fees available to claim at this time.",
	CHAIN_SLIPPAGE_EXCEEDED: "Slippage exceeded \u2014 try increasing your slippage tolerance.",
	CHAIN_INSUFFICIENT_LIQUIDITY: "Insufficient liquidity for this trade size.",
};

/**
 * Get the full error messages map. Uses lazy evaluation for the
 * insufficient balance message so it reads the active chain's currency.
 */
function getChainErrorMessages(): Record<string, string> {
	return {
		...STATIC_CHAIN_ERROR_MESSAGES,
		CHAIN_INSUFFICIENT_BALANCE: `Insufficient balance for gas fees. You need more ${getNativeCurrencySymbol()}.`,
	};
}

const FALLBACK_MESSAGE = "An unexpected error occurred. Please try again.";

/**
 * Map a chain error code to a user-friendly message.
 */
export function getChainErrorMessage(code: string): string {
	return getChainErrorMessages()[code] ?? FALLBACK_MESSAGE;
}

/** Patterns to match error messages to known chain error codes */
const ERROR_PATTERNS: ReadonlyArray<{ pattern: RegExp; code: string }> = [
	{ pattern: /user rejected|user denied|cancelled/i, code: "CHAIN_USER_REJECTED" },
	{ pattern: /insufficient funds|insufficient balance/i, code: "CHAIN_INSUFFICIENT_BALANCE" },
	{ pattern: /gas.*(estimat)/i, code: "CHAIN_GAS_ESTIMATION_FAILED" },
	{ pattern: /NoFeesToClaim|no fees/i, code: "CHAIN_NO_FEES_TO_CLAIM" },
	{
		pattern: /V4TooLittleReceived|TooLittleReceived|SLIPPAGE/i,
		code: "CHAIN_SLIPPAGE_EXCEEDED",
	},
	{
		pattern: /InsufficientLiquidity|insufficient liquidity/i,
		code: "CHAIN_INSUFFICIENT_LIQUIDITY",
	},
	{ pattern: /revert|execution reverted/i, code: "CHAIN_TX_REVERTED" },
];

function matchErrorMessage(message: string): string {
	const messages = getChainErrorMessages();
	if (message in messages) {
		return messages[message] ?? FALLBACK_MESSAGE;
	}

	const matched = ERROR_PATTERNS.find((entry) => entry.pattern.test(message));
	return matched ? (messages[matched.code] ?? FALLBACK_MESSAGE) : FALLBACK_MESSAGE;
}

/**
 * Detect if an error represents a user-initiated wallet rejection.
 */
export function isUserRejection(error: unknown): boolean {
	if (!(error instanceof Error)) return false;
	const msg = error.message.toLowerCase();
	return msg.includes("user rejected") || msg.includes("user denied") || msg.includes("cancelled");
}

/**
 * Attempt to extract a chain error code from an error object and return the user message.
 * Falls back to the generic message if no code is recognized.
 */
export function mapChainError(error: unknown): string {
	if (typeof error === "string") {
		return matchErrorMessage(error);
	}

	if (error instanceof Error) {
		return matchErrorMessage(error.message);
	}

	return FALLBACK_MESSAGE;
}
